// src/utils/factureUtils.js
import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";

// Variables d'environnement
const PHARMACY_NAME = process.env.REACT_APP_PHARMACY_NAME || "PHARMACIE MAME DIARRA BOUSSO";
const OWNER_NAME = process.env.REACT_APP_OWNER_NAME || "DR. Ibrahima Khaliloulaye GNING";
const ADDRESS_LINE1 = process.env.REACT_APP_ADDRESS_LINE1 || "Sor Diagne Route Nationale N 2";
const ADDRESS_LINE2 = process.env.REACT_APP_ADDRESS_LINE2 || "Thies - Tél: +221 33 951 11 65";
const BUSINESS_INFO = process.env.REACT_APP_BUSINESS_INFO || "NINEA 010198837, RC:SN THS 2023 M 2570";
const ADDRESS_EMAIL = process.env.REACT_APP_ADDRESS_EMAIL || "";

// Variables d'environnement pour le feature flipping
const FEATURE_TVA_ENABLED = process.env.REACT_APP_FEATURE_FLIPPING_TVA === 'true';
const TVA_RATE_MOBILE = parseFloat(process.env.REACT_APP_FEATURE_FLIPPING_MOBILE?.replace(',', '.') || '0.005'); // 0,5%
const TVA_RATE_ESPECE = parseFloat(process.env.REACT_APP_FEATURE_FLIPPING_ESPECE?.replace(',', '.') || '0.01'); // 1%


// Fonction pour formater les prix
export const formatPrice = (price) => {
  if (!price && price !== 0) return '0 Fcfa';
  const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
  // Formater avec 2 décimales pour les prix
  const formattedPrice = numericPrice.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${formattedPrice} Fcfa`;
};

// Fonction pour convertir le montant en lettres - CORRIGÉE
export const amountToWords = (amount) => {
  // Arrondir à l'unité pour la conversion en lettres
  const roundedAmount = Math.round(amount);
  
  if (roundedAmount === 0) return 'zéro Francs CFA';
  if (roundedAmount > 999999) return 'Montant trop élevé';

  // Fonction récursive pour convertir les nombres
  const convertNumberToWords = (num) => {
    const units = ['', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf'];
    const teens = ['dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize', 'dix-sept', 'dix-huit', 'dix-neuf'];
    const tens = ['', '', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante', 'soixante-dix', 'quatre-vingt', 'quatre-vingt-dix'];

    if (num < 10) return units[num];
    if (num < 20) return teens[num - 10];
    if (num < 100) {
      const ten = Math.floor(num / 10);
      const unit = num % 10;
      if (unit === 0) {
        if (ten === 8) return tens[ten] + 's'; // "quatre-vingts"
        return tens[ten];
      }
      if (ten === 7 || ten === 9) {
        // Soixante-dix, quatre-vingt-dix
        return tens[ten] + '-' + convertNumberToWords(10 + unit);
      }
      if (ten === 8) {
        return tens[ten] + '-' + units[unit];
      }
      if (unit === 1 && ten !== 8) {
        return tens[ten] + ' et un';
      }
      return tens[ten] + '-' + units[unit];
    }
    if (num < 1000) {
      const hundred = Math.floor(num / 100);
      const remainder = num % 100;
      let result = hundred === 1 ? 'cent' : units[hundred] + ' cent';
      if (remainder === 0 && hundred > 1) result += 's';
      if (remainder > 0) result += ' ' + convertNumberToWords(remainder);
      return result;
    }
    if (num < 1000000) {
      const thousand = Math.floor(num / 1000);
      const remainder = num % 1000;
      let result = thousand === 1 ? 'mille' : convertNumberToWords(thousand) + ' mille';
      if (remainder > 0) result += ' ' + convertNumberToWords(remainder);
      return result;
    }
    return 'Montant trop élevé';
  };

  return convertNumberToWords(roundedAmount) + ' Francs CFA';
};

// Fonction pour détecter si c'est une facture unique ou un rapport
const isSingleInvoice = (venteData) => {
  if (!Array.isArray(venteData) || venteData.length === 0) return false;
  
  // Si toutes les ventes ont le même numéro de facture, c'est une facture unique
  const firstNumFact = venteData[0]?.numFactSrt;
  return venteData.every(vente => vente.numFactSrt === firstNumFact);
};

// Fonction pour calculer la TVA pour un montant
const calculerTVA = (montantHT, modePaiement, remisePourcentage = 0) => {
  if (!FEATURE_TVA_ENABLED) {
    return {
      montantHT: montantHT,
      tva: 0,
      tauxTVA: 0,
      ttc: montantHT,
      netSansTVA: montantHT,
      remiseMontant: 0
    };
  }

  // Calculer la remise
  const remiseMontant = montantHT * (remisePourcentage / 100);
  const netSansTVA = montantHT - remiseMontant;

  // Déterminer le taux de TVA selon le mode de paiement
  let tauxTVA = 0;
  if (modePaiement === 'ESPECE') {
    tauxTVA = TVA_RATE_ESPECE; // 1%
  } else if (modePaiement === 'MOBILE' || modePaiement === 'OM' || modePaiement === 'WAVE') {
    tauxTVA = TVA_RATE_MOBILE; // 0,5% pour mobile, OM et Wave
  }

  const tva = netSansTVA * tauxTVA;
  const ttc = netSansTVA + tva;

  return {
    montantHT: montantHT,
    remiseMontant: remiseMontant,
    netSansTVA: netSansTVA,
    tva: tva,
    tauxTVA: tauxTVA * 100, // Pourcentage pour l'affichage
    ttc: ttc
  };
};

// Fonction pour formater le taux de TVA
const formatTauxTVA = (modePaiement) => {
  if (!FEATURE_TVA_ENABLED) return '0%';
  
  switch(modePaiement) {
    case 'ESPECE':
      return `${(TVA_RATE_ESPECE * 100).toFixed(1)}%`;
    case 'MOBILE':
    case 'OM':
    case 'WAVE':
      return `${(TVA_RATE_MOBILE * 100).toFixed(1)}%`;
    default:
      return '0%';
  }
};

// Fonction pour calculer le prix unitaire TTC
const calculerPrixUnitaireTTC = (prixHT, modePaiement, remisePourcentage = 0) => {
  if (!FEATURE_TVA_ENABLED) return prixHT;
  
  // Calculer la remise sur le prix unitaire (si applicable)
  const remiseMontant = prixHT * (remisePourcentage / 100);
  const prixNetHT = prixHT - remiseMontant;
  
  // Déterminer le taux de TVA
  let tauxTVA = 0;
  if (modePaiement === 'ESPECE') {
    tauxTVA = TVA_RATE_ESPECE;
  } else if (modePaiement === 'MOBILE' || modePaiement === 'OM' || modePaiement === 'WAVE') {
    tauxTVA = TVA_RATE_MOBILE;
  }
  
  return prixNetHT * (1 + tauxTVA);
};

// Fonction pour ajouter la zone de signature/cachet avec gestion de l'espace
const addSignatureStampZone = (doc, yPosition, type = 'facture') => {
  const pageHeight = doc.internal.pageSize.height;
  const pageWidth = doc.internal.pageSize.width;
  const marginBottom = 20; // Marge en bas de page
  const zoneHeight = 45;
  
  // Vérifier s'il reste assez d'espace sur la page courante
  if (yPosition + zoneHeight + marginBottom > pageHeight) {
    // Pas assez d'espace, ajouter une nouvelle page
    doc.addPage();
    yPosition = 30; // Recommencer en haut de la nouvelle page
  }
  
  const marginRight = 20;
  const zoneWidth = 65;
  const zoneX = pageWidth - marginRight - zoneWidth;
  

  // Titre de la zone
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('CACHET & SIGNATURE', zoneX + (zoneWidth/2), yPosition + 20, { align: 'center' });
  
  // Lignes pour signature
  doc.setLineWidth(0.2);
  doc.setDrawColor(100, 100, 100);
  

  
  return { newY: yPosition + zoneHeight + 5, pageAdded: yPosition < 30 };
};

// Fonction utilitaire pour générer une facture de vente
export const factureVente = (venteData, options = {}) => {
  const {
    mode = 'download', // 'download' | 'print' | 'base64'
    fileName = `facture_${venteData[0]?.numFactSrt || 'vente'}_${Date.now()}`
  } = options;

  // Grouper les produits par codeIdProd ET mode de paiement pour additionner les quantités et montants
  const groupedProducts = {};
  
  venteData.forEach(produit => {
    const codeId = produit.codeIdProd;
    const nomProd = produit.nomProd;
    const prix = produit.prixProdVendu || 0;
    const quantite = produit.qtVdu || 0;
    const modePaiement = produit.mode_payement || produit.mode || 'ESPECE';
    const stock = produit.stock || 0;
    // Créer une clé unique par produit ET mode de paiement
    const key = `${codeId}_${modePaiement}`;
    
    if (!groupedProducts[key]) {
      groupedProducts[key] = {
        codeIdProd: codeId,
        nomProd: nomProd,
        prixProdVendu: prix,
        mode_payement: modePaiement,
        totalQuantite: 0,
        totalMontant: 0,
        stock: stock,
        // Garder les informations pour la première occurrence
        dateInsert: produit.dateInsert,
        vendeur: produit.vendeur,
        numFactSrt: produit.numFactSrt
      };
    }
    
    // Additionner les quantités et montants
    groupedProducts[key].totalQuantite += quantite;
    groupedProducts[key].totalMontant += (prix * quantite);
  });

  // Convertir l'objet groupé en tableau et trier par nom de produit
  const groupedData = Object.values(groupedProducts).sort((a, b) => 
    a.nomProd.localeCompare(b.nomProd)
  );

  const doc = new jsPDF();

  // Couleurs simplifiées pour économie d'encre - tout en noir et blanc
  const blackColor = [0, 0, 0];
  const darkGray = [80, 80, 80];

  // Déterminer le type de document
  const isSingle = isSingleInvoice(venteData);
  const documentTitle = isSingle ? 'FACTURE DE VENTE' : 'LISTE DES VENTES';
  const firstProduct = venteData[0] || {};

  // Mode de paiement pour calcul TVA (utiliser le mode du premier produit pour les rapports)
  let modePaiement = 'ESPECE';
  let remisePourcentage = 0;
  
  if (isSingle) {
    // Pour facture unique, utiliser le mode de paiement du premier produit
    modePaiement = firstProduct.mode_payement || firstProduct.mode || 'ESPECE';
    remisePourcentage = firstProduct.remise || 0;
  } else {
    // Pour rapport, utiliser le mode le plus fréquent ou ESPECE par défaut
    const modeCounts = {};
    venteData.forEach(produit => {
      const mode = produit.mode_payement || produit.mode || 'ESPECE';
      modeCounts[mode] = (modeCounts[mode] || 0) + 1;
    });
    
    let maxCount = 0;
    for (const [mode, count] of Object.entries(modeCounts)) {
      if (count > maxCount) {
        maxCount = count;
        modePaiement = mode;
      }
    }
    
    // Pour les rapports, pas de remise globale
    remisePourcentage = 0;
  }

  // En-tête de la facture
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, 210, 60, 'F');

  // Titre principal
  doc.setFontSize(20);
  doc.setTextColor(...blackColor);
  doc.setFont('helvetica', 'bold');
  doc.text(documentTitle, 105, 25, { align: 'center' });

  // Informations pharmacie
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(PHARMACY_NAME, 105, 35, { align: 'center' });
  
  doc.setFontSize(8);
  doc.text(OWNER_NAME, 105, 41, { align: 'center' });
  
  // Construction de l'adresse avec email si disponible
  let addressText = `${ADDRESS_LINE2}`;
  if (ADDRESS_EMAIL) {
    addressText += ` - Email: ${ADDRESS_EMAIL}`;
  }
  doc.text(addressText, 105, 47, { align: 'center' });
  
  doc.text(BUSINESS_INFO, 105, 53, { align: 'center' });

  // Ligne de séparation sous l'en-tête
  doc.setDrawColor(...blackColor);
  doc.setLineWidth(0.5);
  doc.line(15, 62, 195, 62);

  // Informations de la vente
  const dateVente = moment(firstProduct.dateInsert || new Date()).format('DD/MM/YYYY HH:mm');
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...blackColor);
  
  let startY = 70;
  
  // Informations spécifiques pour une facture unique
  if (isSingle) {
    doc.text(`N° Facture: ${firstProduct.numFactSrt || 'N/A'}`, 15, startY);
    doc.text(`Date: ${dateVente}`, 15, startY + 6);
    doc.text(`Vendeur: ${firstProduct.vendeur || 'Non spécifié'}`, 15, startY + 12);
    
    // Informations client pour facture unique
    doc.text(`Client: ${firstProduct.nom || 'Non spécifié'}`, 120, startY);
    // doc.text(`Paiement: ${modePaiement}`, 120, startY + 6);
    
    // Ajouter l'information TVA si activée
    // if (FEATURE_TVA_ENABLED) {
    //   doc.text(`TVA: ${formatTauxTVA(modePaiement)}`, 120, startY + 12);
    // }

    startY += 20;
  } else {
    // Informations pour rapport multiple
    doc.text(`Période: ${options.period || 'Toutes les ventes'}`, 15, startY);
    doc.text(`Date de génération: ${moment().format('DD/MM/YYYY HH:mm')}`, 15, startY + 6);
    doc.text(`Lignes groupées: ${groupedData.length}`, 15, startY + 12);
    doc.text(`Transactions originales: ${venteData.length}`, 15, startY + 18);
    
    // Ajouter l'information TVA si activée pour les rapports
    // if (FEATURE_TVA_ENABLED) {
    //   doc.text(`Mode de paiement principal: ${modePaiement}`, 120, startY);
    //   doc.text(`TVA appliquée: ${formatTauxTVA(modePaiement)}`, 120, startY + 6);
    // }
    
    startY += 24;
  }

  // Préparation des données du tableau
  // Pour les rapports: N° | PRODUIT | QUANTITÉ | PRIX UNITAIRE | TOTAL | DATE | VENDEUR | PAIEMENT
  let tableColumns;
  
  if (isSingle) {
    // Facture unique : tous les produits ont le même mode de paiement
    tableColumns = FEATURE_TVA_ENABLED 
      ? ["PRODUIT", "QUANTITÉ", "PRIX HT", "PRIX TTC", "TOTAL TTC"]
      : ["PRODUIT", "QUANTITÉ", "PRIX UNITAIRE", "TOTAL"];
  } else {
    // Rapport : avec toutes les colonnes demandées
    tableColumns = FEATURE_TVA_ENABLED 
      ? ["PRODUIT", "QUANTITÉ", "STOCK", "PRIX HT", "PRIX TTC", "TOTAL TTC", "DATE", "VENDEUR", "PAIEMENT"]
      : ["PRODUIT", "QUANTITÉ", "STOCK", "PRIX UNITAIRE", "TOTAL", "DATE", "VENDEUR", "PAIEMENT"];
  }


  const tableRows = groupedData.map((produit, index) => {
    const quantite = produit.totalQuantite;
    const prixHT = produit.prixProdVendu;
    const totalHT = produit.totalMontant;
    const produitModePaiement = produit.mode_payement;
    const produitRemisePourcentage = isSingle ? remisePourcentage : 0;
    const dateProduit = moment(produit.dateInsert).format('DD/MM/YYYY');
    // Récupérer le stock depuis les données de produit
    // Si le stock n'est pas disponible dans groupedProducts, mettre 0
    const stockProduit = produit.stock || 0;

    if (FEATURE_TVA_ENABLED) {
      // Avec TVA activée
      const prixTTC = calculerPrixUnitaireTTC(prixHT, produitModePaiement, produitRemisePourcentage);
      const totalTTC = prixTTC * quantite;
      
      if (isSingle) {
        // Facture unique
        return [
          produit.nomProd,
          quantite,
          formatPrice(prixHT),
          formatPrice(prixTTC),
          formatPrice(totalTTC)
        ];
      } else {
        // Rapport
        return [
          produit.nomProd,
          quantite,
          stockProduit,
          formatPrice(prixHT),
          formatPrice(prixTTC),
          formatPrice(totalTTC),
          dateProduit,
          produit.vendeur || 'N/A',
          produitModePaiement
        ];
      }
    } else {
      // Sans TVA
      if (isSingle) {
        // Facture unique
        return [
          produit.nomProd,
          quantite,
          formatPrice(prixHT),
          formatPrice(totalHT)
        ];
      } else {
        // Rapport
        return [
          produit.nomProd,
          quantite,
          stockProduit,
          formatPrice(prixHT),
          formatPrice(totalHT),
          dateProduit,
          produit.vendeur || 'N/A',
          produitModePaiement
        ];
      }
    }
  });

  // Calcul des totaux
  const totals = groupedData.reduce((acc, produit) => {
    const quantite = produit.totalQuantite;
    const prix = produit.prixProdVendu;
    const totalHT = produit.totalMontant;
    const produitModePaiement = produit.mode_payement;
    const produitRemisePourcentage = isSingle ? remisePourcentage : 0;
    
    let totalTTC = totalHT;
    if (FEATURE_TVA_ENABLED) {
      const prixTTC = calculerPrixUnitaireTTC(prix, produitModePaiement, produitRemisePourcentage);
      totalTTC = prixTTC * quantite;
    }
    
    return {
      totalQuantite: acc.totalQuantite + quantite,
      totalMontantHT: acc.totalMontantHT + totalHT,
      totalMontantTTC: acc.totalMontantTTC + totalTTC
    };
  }, { totalQuantite: 0, totalMontantHT: 0, totalMontantTTC: 0 });

  // Génération du tableau
  doc.autoTable({
    startY: startY,
    head: [tableColumns],
    body: tableRows,
    theme: 'grid',
    styles: {
      fontSize: isSingle ? 8 : 7,
      cellPadding: 3,
      lineColor: darkGray,
      lineWidth: 0.1,
      textColor: blackColor,
    },
    headStyles: {
      fillColor: 255,
      textColor: blackColor,
      fontStyle: 'bold',
      fontSize: isSingle ? 8 : 7,
      lineColor: blackColor,
    },
    bodyStyles: {
      textColor: blackColor,
    },
    alternateRowStyles: {
      fillColor: 255,
    },
    margin: { left: 15, right: 15 },
  });

  const finalY = doc.lastAutoTable.finalY + 10;

  // Section des totaux
  doc.setFontSize(10);
  doc.setTextColor(...blackColor);
  doc.setFont('helvetica', 'bold');
 // doc.text('RÉCAPITULATIF', 15, finalY);

  // Calculer les totaux avec TVA
  let calculTVA;
  if (isSingle) {
    calculTVA = calculerTVA(totals.totalMontantHT, modePaiement, remisePourcentage);
  } else {
    calculTVA = calculerTVA(totals.totalMontantHT, modePaiement, 0);
  }

  doc.setFontSize(9);
  doc.setTextColor(...blackColor);
  doc.setFont('helvetica', 'normal');
  
  let yPosition = finalY + 8;
  
  // doc.text(`Lignes groupées: ${groupedData.length}`, 15, yPosition);
  // yPosition += 8;
  
  // doc.text(`Quantité totale: ${totals.totalQuantite}`, 15, yPosition);
  // yPosition += 8;
  
  // doc.text(`Sous-total HT: ${formatPrice(totals.totalMontantHT)}`, 15, yPosition);
  // yPosition += 8;

  // // Afficher la remise si applicable (uniquement pour facture unique)
  // if (isSingle && remisePourcentage > 0) {
  //   doc.text(`Remise (${remisePourcentage}%): -${formatPrice(calculTVA.remiseMontant)}`, 15, yPosition);
  //   yPosition += 8;
    
  //   doc.text(`Net HT: ${formatPrice(calculTVA.netSansTVA)}`, 15, yPosition);
  //   yPosition += 8;
  // }

  // Afficher la TVA si feature activé
  // if (FEATURE_TVA_ENABLED) {
  //   doc.text(`TVA (${formatTauxTVA(modePaiement)}): +${formatPrice(calculTVA.tva)}`, 15, yPosition);
  //   yPosition += 8;
  // }

  // Montant final
  doc.setFont('helvetica', 'bold');
  const montantFinal = FEATURE_TVA_ENABLED ? calculTVA.ttc : totals.totalMontantHT;
  const labelTotal = FEATURE_TVA_ENABLED ? 'TOTAL' : 'TOTAL GÉNÉRAL';
  //doc.text(`${labelTotal}: ${formatPrice(montantFinal)}`, 15, yPosition);
  //yPosition += 8;

  // Montant en lettres (uniquement pour facture unique)
  if (isSingle) {
    try {
      doc.setFontSize(8);
      doc.setTextColor(...blackColor);
      doc.setFont('helvetica', 'italic');
      doc.text(`${labelTotal}: ${formatPrice(montantFinal)}`, 195, yPosition, { align: 'right' });
      yPosition += 8;
      const montantPourLettres = Math.round(montantFinal);
      const montantEnLettres = amountToWords(montantPourLettres);
      doc.text(`Arrêtée à la somme de: ${montantEnLettres.toUpperCase()}`, 15, yPosition);
      yPosition += 8;
    } catch (error) {
      console.error("Erreur lors de la conversion du montant en lettres:", error);
      doc.text(`Arrêtée à la somme de: ${formatPrice(montantFinal)}`, 15, yPosition);
      yPosition += 8;
    }
  }

  // Informations de monnaie rendue pour paiement en espèces (uniquement pour facture unique)
  if (isSingle && modePaiement === 'ESPECE') {
    const montantVerse = firstProduct.montantVerse || firstProduct.donne || montantFinal;
    //const monnaieRendue = firstProduct.monnaieRendue || firstProduct.rendu || (montantVerse - montantFinal);
    
    if (montantVerse > 0) {
      doc.setFontSize(8);
      doc.setTextColor(...blackColor);
      doc.setFont('helvetica', 'normal');
     // doc.text(`Montant reçu: ${formatPrice(montantVerse)}`, 15, yPosition);
     // yPosition += 6;
      
      // if (monnaieRendue > 0) {
      //   doc.text(`Monnaie rendue: ${formatPrice(monnaieRendue)}`, 15, yPosition);
      //   yPosition += 6;
      // }
    }
  }

  // Calculer la position pour la zone de signature
  let signatureY;
  if (isSingle) {
    signatureY = finalY + 10; // Pour facture unique, la zone signature commence plus haut
  } else {
    signatureY = finalY + 30; // Pour les rapports, la zone signature est plus basse
  }

  // Vérifier si la zone de signature dépasse de la page
  const pageHeight = doc.internal.pageSize.height;
  const zoneHeight = 35;
  const marginBottom = 20;
  
  // Si la zone de signature dépasse, on l'ajoute sur une nouvelle page
  if (signatureY + zoneHeight + marginBottom > pageHeight) {
    doc.addPage();
    signatureY = 30; // Recommencer en haut de la nouvelle page
  }

  // Ajouter la zone de signature/cachet à droite
  const signatureType = isSingle ? 'facture' : 'rapport';
  const signatureResult = addSignatureStampZone(doc, signatureY, signatureType);
  
  // Mettre à jour yPosition si une nouvelle page a été ajoutée
  if (signatureResult.pageAdded) {
    yPosition = signatureResult.newY;
  } else {
    yPosition = Math.max(yPosition, signatureResult.newY);
  }

  // Information sur le feature flipping TVA
  if (FEATURE_TVA_ENABLED) {
    doc.setFontSize(6);
    doc.setTextColor(...darkGray);
    doc.setFont('helvetica', 'italic');
    
    if (isSingle) {
      doc.text(`Système TVA activé - Taux: Espèce ${(TVA_RATE_ESPECE * 100).toFixed(1)}% / Mobile/OM/WAVE ${(TVA_RATE_MOBILE * 100).toFixed(1)}%`, 
        105, yPosition, { align: 'center' });
    } else {
      // Afficher les statistiques détaillées par mode de paiement
      const modeStats = {};
      const modeQuantite = {};
      const modeMontant = {};
      
      groupedData.forEach(produit => {
        const mode = produit.mode_payement;
        modeStats[mode] = (modeStats[mode] || 0) + 1;
        modeQuantite[mode] = (modeQuantite[mode] || 0) + produit.totalQuantite;
        modeMontant[mode] = (modeMontant[mode] || 0) + produit.totalMontant;
      });
      
      const statsText = Object.entries(modeStats)
        .map(([mode, count]) => `${mode}: ${count} lignes`)
        .join(' | ');
      
      doc.text(`Répartition: ${statsText}`, 105, yPosition, { align: 'center' });
      yPosition += 4;
      
      const quantiteText = Object.entries(modeQuantite)
        .map(([mode, qte]) => `${mode}: ${qte} unités`)
        .join(' | ');
      
      doc.text(`Quantités par mode: ${quantiteText}`, 105, yPosition, { align: 'center' });
    }
    yPosition += 6;
  }

  // Pied de page
  const finalPageHeight = doc.internal.pageSize.height;
  doc.setFontSize(7);
  doc.setTextColor(...blackColor);
  doc.text(
    `Merci pour votre confiance ! - ${PHARMACY_NAME}`,
    105,
    finalPageHeight - 10,
    { align: 'center' }
  );

  // Retour selon le mode
  switch (mode) {
    case 'print':
      doc.autoPrint();
      doc.output('dataurlnewwindow');
      break;
    case 'base64':
      return doc.output('datauristring');
    case 'download':
    default:
      doc.save(`${fileName}.pdf`);
      break;
  }
};

export default factureVente;